<template>
  <div class="min-h-screen text-foreground pt-20 relative z-20">
    <div class="container mx-auto py-12 text-center px-4">
      <!-- Profile Picture -->
      <div class="flex justify-center mb-4">
        <img :src="profileImage" alt="Profile"
          class="profile-picture rounded-full border-4 border-teal-500 shadow-lg w-32 h-32 md:w-48 md:h-48" />
      </div>

      <!-- Typewriter Text (no animation) -->
      <div class="text-2xl font-light text-white mb-4">
        <p class="text-2xl font-light text-white mb-8">{{ $t('hiThere') }}</p>
      </div>

      <!-- Name and Title (no animation) -->
      <p class="text-4xl md:text-5xl font-bold text-white mb-4">
        {{ $t('myNameIs') }} <span class="text-teal-400">Adem Bessam</span>
      </p>
      <p class="text-2xl font-light text-white mb-8">
        {{ $t('iAmA') }} {{ $t('fullStackDeveloper') }}
      </p>

      <!-- Let's Connect Button -->
      <!-- Social Links (GitHub & LinkedIn) -->
      <div class="flex justify-center mb-6 px-4">
        <div class="w-full max-w-xl">
          <div class="flex flex-col md:flex-row items-center md:justify-center gap-4">
            <!-- GitHub -->
            <a href="https://github.com/Hares-2088" target="_blank" rel="noopener noreferrer"
              class="w-full md:w-auto flex items-center md:flex-row flex-col md:space-x-3 bg-gray-800/60 hover:bg-gray-800/80 border border-gray-700 rounded-lg px-4 py-3 transition-colors duration-200">
              <!-- Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-8 h-8 text-white mb-2 md:mb-0">
                <path fill="currentColor"
                  d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.387.6.113.82-.263.82-.583 0-.288-.01-1.05-.016-2.06-3.338.726-4.042-1.61-4.042-1.61C4.422 17.07 3.633 16.7 3.633 16.7c-1.086-.742.083-.727.083-.727 1.203.085 1.837 1.236 1.837 1.236 1.067 1.828 2.8 1.3 3.486.995.108-.774.418-1.3.76-1.6-2.665-.303-5.467-1.332-5.467-5.93 0-1.31.468-2.38 1.235-3.22-.124-.303-.535-1.523.117-3.176 0 0 1.007-.322 3.3 1.23a11.5 11.5 0 0 1 3.003-.404c1.02.004 2.047.137 3.003.404 2.29-1.552 3.295-1.23 3.295-1.23.655 1.653.244 2.873.12 3.176.77.84 1.234 1.91 1.234 3.22 0 4.61-2.807 5.624-5.48 5.92.43.37.815 1.1.815 2.22 0 1.6-.015 2.89-.015 3.28 0 .323.216.698.825.58C20.565 21.795 24 17.298 24 12c0-6.63-5.373-12-12-12z" />
              </svg>

              <!-- Text -->
              <div class="text-left">
                <div class="text-white font-semibold">GitHub</div>
                <div class="text-gray-400 text-sm">@Hares-2088</div>
              </div>
            </a>

            <!-- LinkedIn -->
            <a href="https://www.linkedin.com/in/adem-bessam/" target="_blank" rel="noopener noreferrer"
              class="w-full md:w-auto flex items-center md:flex-row flex-col md:space-x-3 bg-blue-900/60 hover:bg-blue-900/80 border border-blue-800 rounded-lg px-4 py-3 transition-colors duration-200">
              <!-- Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-8 h-8 text-white mb-2 md:mb-0">
                <path fill="currentColor"
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.849-3.037-1.853 0-2.136 1.446-2.136 2.939v5.667H9.357V9h3.414v1.561h.049c.476-.9 1.637-1.849 3.37-1.849 3.603 0 4.269 2.372 4.269 5.456v6.284zM5.337 7.433a2.07 2.07 0 1 1 0-4.139 2.07 2.07 0 0 1 0 4.139zM6.987 20.452H3.689V9h3.298v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.728v20.543C0 23.228.792 24 1.771 24h20.451C23.2 24 24 23.228 24 22.271V1.728C24 .774 23.2 0 22.225 0z" />
              </svg>

              <!-- Text -->
              <div class="text-left">
                <div class="text-white font-semibold">LinkedIn</div>
                <div class="text-gray-200 text-sm">Adem Bessam</div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <div class="flex ">
        <transition name="fade-up" appear>
          <ContactForm />
        </transition>
      </div>

      <!-- Download Resume Button -->
      <div class="mt-8">
        <a :href="resumeLink" :download="resumeFileName"
          class="px-6 py-2 border border-teal-500 text-teal-500 rounded-md hover:bg-teal-500 hover:text-white transition duration-300">
          {{ $t('downloadResume') }}
        </a>
      </div>
    </div>

    <!-- Add Comment Section -->
    <div class="mt-12 container mx-auto px-4">
      <h2 class="text-2xl md:text-3xl font-bold text-white mb-4">{{ $t('leaveComment') }}</h2>
      <form @submit.prevent="submitComment" class="mt-6">
        <div v-if="!isAuthenticated" class="mb-2">
          <input v-model="guestName" type="text" :placeholder="$t('yourName')"
            class="w-full p-2 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required />
        </div>
        <textarea v-model="newComment" :placeholder="$t('addComment')"
          class="w-full p-2 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows="3" required></textarea>
        <button type="submit"
          class="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300">
          {{ $t('submitComment') }}
        </button>
      </form>
      <div v-if="commentSuccessMessage" class="mt-2 text-green-400">
        {{ commentSuccessMessage }}
      </div>
      <div v-if="isAuthenticated" class="mt-2 text-gray-400">
        {{ $t('commentingAs') }}: {{ user.name }}
      </div>
    </div>

    <!-- Approved Comments Section -->
    <div class="mt-12 container mx-auto px-4">
      <h2 class="text-2xl md:text-3xl font-bold text-white mb-4">{{ $t('comments') }}</h2>
      <div v-if="approvedComments.length === 0" class="text-gray-400">
        {{ $t('noComments') }}
      </div>
      <div v-else>
        <div v-for="comment in approvedComments" :key="comment.id"
          class="comment-container bg-gray-900/70 backdrop-blur-md rounded-lg p-6 mb-4 border border-gray-700">
          <p class="comment-author"><strong>{{ comment.author }}</strong> says:</p>
          <p class="comment-content">{{ comment.content }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
// import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faHandshake } from '@fortawesome/free-solid-svg-icons';
import { addComment, fetchComments } from '@/api/comments'; // Import addComment and getApprovedComments functions
import { useAuth0 } from '@auth0/auth0-vue'; // Import useAuth0
import { useI18n } from 'vue-i18n'; // Import useI18n
import ContactForm from "@/components/ContactForm.vue";


library.add(faHandshake);

export default {
  components: {
    ContactForm,
    //   FontAwesomeIcon,
  },
  setup() {
    const { locale } = useI18n(); // Use useI18n to get the current locale
    const profileImage = require('@/assets/profile.png');
    const newComment = ref('');
    const guestName = ref('');
    const { isAuthenticated, user } = useAuth0(); // Use Auth0
    const approvedComments = ref([]);
    const commentSuccessMessage = ref('');

    const resumeLink = computed(() => locale.value === 'fr' ? '/Adem_Bessam_CV.pdf' : '/Resume.pdf');
    const resumeFileName = computed(() => locale.value === 'fr' ? 'Adem_Bessam_CV.pdf' : 'Adem_Resume.pdf');

    onMounted(() => {
      fetchApprovedComments(); // Fetch approved comments on mount
    });


    const submitComment = async () => {
      try {
        let authorName = isAuthenticated.value ? user.value.name : guestName.value.trim();
        if (!authorName) {
          alert('Please enter your name.');
          return;
        }
        const commentData = {
          content: newComment.value,
          project_id: '67b416a5058e97052852e5e5',
          author: authorName,
          approved: false
        };
        await addComment(commentData); // Call addComment without token
        newComment.value = '';
        guestName.value = '';
        commentSuccessMessage.value = `Thank you${authorName ? ', ' + authorName : ''}! Your comment has been submitted and is awaiting approval.`;
        setTimeout(() => { commentSuccessMessage.value = ''; }, 5000);
      } catch (error) {
        console.error('Error submitting comment:', error);
        alert('Failed to submit comment.');
      }
    };

    const fetchApprovedComments = async () => {
      try {
        const comments = await fetchComments('67b416a5058e97052852e5e5');
        approvedComments.value = comments;
      } catch (error) {
        console.error('Error fetching approved comments:', error);
      }
    };

    return {
      profileImage,
      newComment,
      guestName,
      submitComment,
      isAuthenticated,
      user,
      approvedComments,
      resumeLink,
      resumeFileName,
      commentSuccessMessage
    };
  },
};
</script>

<style scoped>
.profile-picture {
  width: 256px;
  height: 256px;
  object-fit: cover;
  object-position: 0% 70%;
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: opacity 1s, transform 1s;
}

.fade-up-enter,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.comment-container {
  background-color: rgba(31, 41, 55, 0.7);
  /* gray-900 with opacity */
  backdrop-filter: blur(10px);
  border: 1px solid #374151;
  /* gray-700 */
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.comment-author {
  color: #4fd1c5;
  /* teal-400 */
  font-weight: 600;
}

.comment-content {
  color: #d1d5db;
  /* gray-300 */
  margin-top: 0.5rem;
}
</style>